import {
  createContext,
  useCallback,
  useMemo,
  useState,
} from 'react';
import { PaginationMetaLink } from '../models/Paginator';
import { PaginationMeta } from '../components/Modals/Paginator';
import api from '../services/api';
import ModalComponent from '../components/ModalComponent';
import FileManagerUtils from '../utils/FileManagerUtils';

export interface BasicFileInterface {
  file_name: string
  id?: number
  path?: string
  size?: number
  extension?: string
}

export type FileInterface = BasicFileInterface;

interface FileContextProps {
  total: number
  files?: FileInterface[]
  currentPage?: number
  links?: PaginationMetaLink[]
  paginationPageInfo?: PaginationMeta
  errors?: any
  create?: (file: File, cb?: () => void, cbe?: () => void) => void
  edit?: (fileId: number, file: File, cb?: () => void, cbe?: () => void) => void
  deleteFile?: (fileId: number, cb?: () => void) => void
  fetch?: (page?: number) => void
  rename?: (fileId: number, newFileName: string, cb?: () => void) => void;
  getDownloaddableFileUrl?: (fileName: string) => string
  isLoading?: boolean,
  clearErrors?: () => void
}

export const FileContext = createContext<FileContextProps>({ total: 0 });

interface Props {
  children: JSX.Element
}

function FilesProvider({ children }: Props) {
  const [total, setTotal] = useState(0);
  const [links, setLinks] = useState(null);
  const [files, setFiles] = useState([] as FileInterface[]);
  const [currentPage] = useState(1);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [paginationPageInfo, setPaginationPageInfo] = useState({
    current_page: 1,
    from: 1,
    last_page: 1,
    links: [],
    path: '',
    per_page: 1,
    to: 1,
    total: 1,
  });

  // eslint-disable-next-line no-spaced-func
  const [modalProperties, setModalProperties] = useState<{
    isOpen: boolean,
    fn: () => void
  }>({ isOpen: false, fn: () => { } });

  const fetch = useCallback(async (page?: number) => {
    setIsLoading(true);

    try {
      const result = await api.get(`/v1/files${page ? `?page=${page}` : ''}`);

      setPaginationPageInfo(result.data.meta);
      setTotal(result.data.meta.total);
      setLinks(result.data.meta.links);
      setFiles(result.data.data);
    } catch (e: any) {
      console.error(e);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const create = useCallback(async (
    file: File,
    cb?: () => void,
    cbe?: () => void,
  ) => {
    if (!FileManagerUtils.checkFileExtension(file, setErrors)) return;

    setIsLoading(true);

    try {
      const formData = new FormData();
      formData.append('file', file as any);
      await api.post('/v1/files/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }).then(() => {
        if (cb) cb();
        fetch(currentPage);
      });
    } catch (e: any) {
      if (e.response && e.response.data) {
        const { errors, message } = e.response.data;
        setErrors(errors || { file: message || 'エラーが発生しました。' });
      } else {
        setErrors({ file: 'エラーが発生しました。' });
      }
      if (cbe) cbe();
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, fetch]);

  const edit = useCallback(async (
    fileId: number,
    file: File,
    cb?: () => void,
    cbe?: () => void,
  ) => {
    if (!FileManagerUtils.checkFileExtension(file, setErrors)) return;

    setIsLoading(true);

    try {
      const formData = new FormData();
      formData.append('file', file as any);

      await api.post(`/v1/files/edit/${fileId}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }).then(() => {
        if (cb) cb();
        fetch(currentPage);
      });
    } catch (e: any) {
      if (e.response && e.response.data) {
        const { errors, message } = e.response.data;
        setErrors(errors || { file: message || 'エラーが発生しました。' });
      } else {
        setErrors({ file: 'エラーが発生しました。' });
      }
      if (cbe) cbe();
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, fetch]);

  const deleteFile = useCallback(async (fileId: number, cb?: () => void) => {
    setIsLoading(true);
    try {
      await api.delete(`/v1/files/${fileId}`)
        .then(() => {
          if (cb) cb();
          fetch(currentPage);
        });
    } catch (e) {
      console.error(e);
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, fetch]);

  const rename = useCallback(async (fileId: number, newFileName: string, cb?: () => void) => {
    setIsLoading(true);
    try {
      await api.post(`/v1/files/rename/${fileId}`, { newFileName });
      if (cb) cb();
      fetch(currentPage);
    } catch (e: any) {
      if (e.response && e.response.data) {
        const { errors, message } = e.response.data;
        setErrors(errors || { file: message || 'エラーが発生しました。' });
      } else {
        setErrors({ file: 'エラーが発生しました。' });
      }
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, fetch]);

  const getDownloaddableFileUrl = useCallback((fileName: string) => `files/${fileName}`, []);

  const clearErrors = useCallback(() => {
    setErrors({});
  }, []);

  const value = useMemo<FileContextProps>(() => ({
    total,
    links,
    files,
    currentPage,
    paginationPageInfo,
    errors,
    create,
    fetch,
    edit,
    deleteFile,
    rename,
    getDownloaddableFileUrl,
    isLoading,
    clearErrors,
  }), [total, links, files, currentPage, paginationPageInfo, errors, create, fetch, edit, deleteFile, rename, getDownloaddableFileUrl, isLoading, clearErrors]);

  return (
    <>
      <FileContext.Provider value={value}>
        {children}
      </FileContext.Provider>

      <ModalComponent
        centered
        title="確認"
        show={modalProperties.isOpen}
        onConfirm={modalProperties.fn}
        confirmButtonText="上書き"
        closeModal={() => { setModalProperties({ ...modalProperties, isOpen: false }); setIsLoading(false); }}
        backdropClassName="z-index-3000"
        style={{ zIndex: 3000 }}
      >
        ファイルを上書きします。よろしいですか？
      </ModalComponent>
    </>
  );
}

export default FilesProvider;
